import React, { useState, useRef, useEffect, useMemo } from "react";
import { useAnalytics } from '@/hooks/useAnalytics';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, RotateCcw, X, Play, Pause, Loader2 } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import bookLogo from "@/assets/book-logo.png";
import { useOracleCardsData, type OracleCard } from '@/hooks/useOracleCardsData';

export { OracleCardsFan };

const OracleCardsFan = () => {
  const { cards: oracleCards, loading, isUsingFallback } = useOracleCardsData();
  const { trackFeatureUsage } = useAnalytics();
  const [selectedCard, setSelectedCard] = useState<OracleCard | null>(null);
  const [highlightedCardId, setHighlightedCardId] = useState<string | null>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const [carouselApi, setCarouselApi] = useState<any>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  
  // Shuffle cards once on mount for variety
  const shuffledCards = useMemo(() => {
    if (!oracleCards.length) return [];
    const shuffled = [...oracleCards];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, [oracleCards]);

  const handleCardSelect = (card: OracleCard) => {
    setSelectedCard(card);
    trackFeatureUsage('oracle_card', 'selected', {
      card_title: card.title,
      card_suit: card.suit
    });
  };

  const handleCloseCard = () => {
    setSelectedCard(null);
  };

  const handleNewReading = () => {
    setSelectedCard(null);
    setHighlightedCardId(null);
  };

  const startAutoScroll = () => {
    if (!carouselApi || shuffledCards.length === 0) return;
    
    setIsScrolling(true);
    trackFeatureUsage('oracle_divine_guidance', 'started');
    
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Start scrolling through cards
    intervalRef.current = setInterval(() => {
      if (carouselApi) {
        carouselApi.scrollNext();
      }
    }, 150); // Fast scroll speed

    // Stop after 3-5 seconds randomly
    const stopTime = 3000 + Math.random() * 2000;
    setTimeout(() => {
      stopAutoScroll();
    }, stopTime);
  };

  const stopAutoScroll = () => {
    setIsScrolling(false);
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Select the card that's in the center of the carousel
    setTimeout(() => {
      if (carouselApi && shuffledCards.length > 0) {
        const currentIndex = carouselApi.selectedScrollSnap();
        // Ensure it's within bounds
        const chosenIndex = currentIndex % shuffledCards.length;
        setLastSelectedIndex(chosenIndex);
        const divinelyChosenCard = shuffledCards[chosenIndex];
        if (divinelyChosenCard) {
          // Highlight the card that's already in the center
          setHighlightedCardId(divinelyChosenCard.id);

          // Flash the chosen card with divine energy before opening
          setTimeout(() => {
            setHighlightedCardId(null);
            handleCardSelect(divinelyChosenCard);
          }, 1500);
        }
      }
    }, 300);
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Stop auto scroll when isScrolling changes to false
  useEffect(() => {
    if (!isScrolling && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [isScrolling]);

  if (loading) {
    return (
      <div className="w-full max-w-5xl mx-auto p-6">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground mt-2">Loading oracle cards...</p>
        </div>
      </div>
    );
  }

  if (selectedCard) {
    return (
      <div className="w-full max-w-2xl mx-auto p-6">
        <Card className="relative bg-gradient-mystical border-primary/20 shadow-oracle overflow-hidden">
          <div className="absolute inset-0 bg-gradient-oracle opacity-50" />
          
          {/* Close button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCloseCard}
            className="absolute top-4 right-4 z-20 bg-background/80 hover:bg-background/90 backdrop-blur-sm"
          >
            <X className="h-4 w-4" />
          </Button>
          
          <div className="relative z-10 p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 text-primary font-medium text-sm mb-2">
                <Sparkles className="h-4 w-4" />
                {selectedCard.suit}
              </div>
              <h2 className="text-3xl font-bold text-foreground mb-4">
                {selectedCard.title}
              </h2>
              <p className="text-lg text-muted-foreground italic mb-6">
                "{selectedCard.messageFromMirror}"
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">What This Reflects</h3>
                <p className="text-foreground/90 leading-relaxed">
                  {selectedCard.whatThisReflects}
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">Healing Invitation</h3>
                <p className="text-foreground/90 leading-relaxed mb-4">
                  {selectedCard.healingInvitation}
                </p>
                <div className="bg-primary/10 p-4 rounded-lg">
                  <p className="text-foreground font-medium italic">
                    "{selectedCard.affirmation}"
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">Key Themes</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedCard.keywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm font-medium"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <Button
                onClick={handleNewReading}
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium px-6 py-2"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Draw Another Card
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto p-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-light text-foreground mb-1">
          The <span className="font-normal text-primary">Mirror</span> of <span className="font-light tracking-wide">Meaning</span>
        </h2>
        <p className="text-sm text-primary font-medium mb-2">QuantumSelf<sup className="text-[0.6em]">™</sup> Oracle Deck</p>
        <p className="text-muted-foreground">
          Browse the cards and choose one to reveal your guidance
        </p>
        {isUsingFallback && (
          <p className="text-xs text-muted-foreground/70 italic mt-1">
            (Using offline mode)
          </p>
        )}
      </div>

      {/* Auto-scroll controls */}
      <div className="text-center mb-6">
        <Button
          onClick={isScrolling ? stopAutoScroll : startAutoScroll}
          className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium px-6 py-2"
        >
          {isScrolling ? (
            <>
              <Pause className="h-4 w-4 mr-2" />
              Stop Divine Guidance
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-2" />
              Let the Universe Choose
            </>
          )}
        </Button>
      </div>

      <Carousel
        className="w-full max-w-4xl mx-auto"
        setApi={setCarouselApi}
        opts={{
          align: "center",
          loop: true,
          duration: 15
        }}
      >
        <CarouselContent className="-ml-2 md:-ml-4">
          {shuffledCards.map((card, index) => (
            <CarouselItem
              key={card.id}
              className="pl-2 md:pl-4 basis-1/2 md:basis-1/3 lg:basis-1/5"
            >
              <div className="p-2">
                {/* Oracle card with standard 3.5:5 proportions (7:10 ratio) and border */}
                <Card
                  className={`w-full bg-gradient-mystical border-2 shadow-oracle hover:shadow-primary/20 transition-all duration-300 cursor-pointer overflow-hidden hover:scale-105 ${
                    highlightedCardId === card.id
                      ? 'border-primary shadow-[0_0_30px_hsl(var(--primary)/0.8)] animate-pulse scale-110 z-10'
                      : 'border-primary/40'
                  }`}
                  onClick={() => handleCardSelect(card)}
                  style={{ aspectRatio: '7/10' }}
                >
                  <div
                    className={`relative w-full h-full flex flex-col items-center justify-between p-4 border-2 border-primary/20 m-1 rounded-md ${
                      highlightedCardId === card.id ? 'bg-primary/10' : ''
                    }`}
                  >
                    <div className="absolute inset-0 bg-gradient-oracle opacity-30" />
                    
                    {/* Mystical sparkles for highlighted card */}
                    {highlightedCardId === card.id && (
                      <>
                        <div className="absolute top-2 left-2 animate-bounce">
                          <Sparkles className="h-3 w-3 text-primary opacity-80" />
                        </div>
                        <div className="absolute top-4 right-3 animate-bounce delay-200">
                          <Sparkles className="h-2 w-2 text-primary opacity-70" />
                        </div>
                        <div className="absolute bottom-6 left-3 animate-bounce delay-100">
                          <Sparkles className="h-2 w-2 text-primary opacity-90" />
                        </div>
                        <div className="absolute bottom-3 right-2 animate-bounce delay-300">
                          <Sparkles className="h-3 w-3 text-primary opacity-60" />
                        </div>
                      </>
                    )}
                    
                    {/* Logo positioned in center, bigger */}
                    <div className="flex-1 flex items-center justify-center">
                      <img
                        src={bookLogo}
                        alt="The Mirror of Meaning Card Back"
                        className={`w-24 h-24 md:w-32 md:h-32 opacity-90 relative z-10 transition-all duration-500 object-contain ${
                          highlightedCardId === card.id ? 'scale-110 brightness-125' : ''
                        }`}
                        style={{
                          filter: `drop-shadow(0 0 8px hsl(var(--primary) / ${highlightedCardId === card.id ? '1' : '0.6'}))`,
                          aspectRatio: '1/1'
                        }}
                      />
                    </div>
                    
                    {/* Gold text at bottom */}
                    <div className="relative z-10 text-center pb-2">
                      <div
                        className={`text-xs md:text-sm font-light leading-tight transition-all duration-500 ${
                          highlightedCardId === card.id ? 'scale-110' : ''
                        }`}
                        style={{
                          background: 'var(--gold-shine)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          textShadow: `0 0 4px hsl(var(--primary) / ${highlightedCardId === card.id ? '1' : '0.8'})`,
                          fontFamily: 'Aileron, sans-serif'
                        }}
                      >
                        The <span className="font-normal">Mirror</span><br />of Meaning
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="left-4" />
        <CarouselNext className="right-4" />
      </Carousel>
      
      <div className="text-center mt-8">
        <p className="text-sm text-muted-foreground">
          Browse through all 55 oracle cards - use the divine guidance button or click any card that calls to you
        </p>
      </div>
    </div>
  );
};

export default OracleCardsFan;
